/**
 * Entities API - Complete implementation matching trace-api.alpyne.ai/docs
 */

import { Entity, EntityQueryRequest, EntityQueryResponse } from '../types/api';
import { getAPIClient } from './client';

// Query entities
export async function queryEntities(request: EntityQueryRequest): Promise<EntityQueryResponse> {
  const client = getAPIClient();
  return client.post<EntityQueryResponse>('/entities/query', request);
}

// Bulk query multiple entity types (NEW - more efficient than N individual calls)
export interface BulkEntityQueryRequest {
  project_id: string;
  entity_types: string[];
  filters_per_type?: Record<string, Record<string, any>>;
  global_filters?: Record<string, any>;
  limit_per_type?: number; // Default: 100, Max: 100
  offset_per_type?: number; // Default: 0 (for pagination)
  sort_by?: string;
  order?: 'asc' | 'desc';
}

export interface EntityTypeResult {
  entity_type: string;
  entities: Entity[];
  total_count: number;
  returned_count: number; // Number of entities in this page
  has_more: boolean; // Whether more results exist
  filters_applied: Record<string, any>;
  execution_time_ms: number;
}

export interface BulkEntityQueryResponse {
  project_id: string;
  results: EntityTypeResult[];
  total_entity_types: number;
  total_entities_returned: number;
  execution_time_ms: number;
}

export async function queryEntitiesBulk(request: BulkEntityQueryRequest): Promise<BulkEntityQueryResponse> {
  const client = getAPIClient();
  console.log(`[API] Bulk querying ${request.entity_types.length} entity types for project ${request.project_id}`);
  console.log(`[API] Entity types: ${request.entity_types.join(', ')}`);
  
  const response = await client.post<BulkEntityQueryResponse>('/entities/query-bulk', request);
  
  console.log(`[API] Bulk query complete: ${response.total_entity_types} types with results, ` +
              `${response.total_entities_returned} total entities in ${response.execution_time_ms.toFixed(1)}ms`);
  
  return response;
}

// Get distinct sources for an entity type
export async function getEntitySources(
  projectId: string,
  entityType: string,
  options?: { geographic_zone?: string }
): Promise<{ sources: Array<{ source_id: string; source_name: string | null; integration_type: string | null; entity_count: number }>; total: number }> {
  const client = getAPIClient();
  return client.get('/entities/sources', {
    params: {
      project_id: projectId,
      entity_type: entityType,
      geographic_zone:
        options?.geographic_zone && options.geographic_zone !== 'all'
          ? options.geographic_zone
          : undefined,
    },
  });
}

// Batch get entity counts for multiple artifacts (optimized - uses dedicated backend endpoint)
export async function getEntityCountsBatch(
  projectId: string,
  artifactIds: string[]
): Promise<Record<string, number>> {
  if (artifactIds.length === 0) {
    return {};
  }

  const client = getAPIClient();
  
  try {
    // Use the new dedicated /entities/counts endpoint which uses GROUP BY for efficiency
    const response = await client.get<{ counts: Record<string, number> }>('/entities/counts', {
      params: {
        project_id: projectId,
        artifact_ids: artifactIds.join(','), // Comma-separated list
      },
    });

    return response.counts || {};
  } catch (error) {
    console.error('[getEntityCountsBatch] Error fetching entity counts:', error);
    // Return zeros on error
    const counts: Record<string, number> = {};
    artifactIds.forEach(id => {
      counts[id] = 0;
    });
    return counts;
  }
}

// Export entities
export async function exportEntities(
  projectId: string,
  entityType: string,
  filters?: Record<string, any>,
  format: 'csv' | 'json' | 'xlsx' = 'csv',
  template?: string
): Promise<Blob> {
  const client = getAPIClient();
  return client.get<Blob>('/entities/export', {
    params: {
      project_id: projectId,
      entity_type: entityType,
      filters: filters ? JSON.stringify(filters) : undefined,
      format,
      template: template || undefined,
    },
    responseType: 'blob',
  });
}

// Bulk create entities
export async function bulkCreateEntities(
  projectId: string,
  entities: Array<{ entity_type: string; data: Record<string, any> }>
): Promise<{ created: number }> {
  const client = getAPIClient();
  return client.post<{ created: number }>('/entities/bulk', {
    project_id: projectId,
    entities,
  });
}

// Create single entity
export async function createEntity(
  projectId: string,
  entityType: string,
  data: Record<string, any>
): Promise<Entity> {
  const client = getAPIClient();
  return client.post<Entity>('/entities/create', {
    project_id: projectId,
    entity_type: entityType,
    data,
  });
}

// List entities
export async function listEntities(
  projectId: string,
  entityType?: string,
  limit: number = 100
): Promise<Entity[]> {
  const client = getAPIClient();
  return client.get<Entity[]>('/entities', {
    params: {
      project_id: projectId,
      entity_type: entityType,
      limit,
    },
  });
}

// Get single entity
export async function getEntity(entityId: string): Promise<Entity> {
  const client = getAPIClient();
  return client.get<Entity>(`/entities/${entityId}`);
}

// Update entity (partial update: merges data with existing)
export interface UpdateEntityRequest {
  data?: Record<string, any>;
  entity_type?: string;
  source_artifact_id?: string;
}

export async function updateEntity(
  entityId: string,
  request: UpdateEntityRequest
): Promise<Entity> {
  const client = getAPIClient();
  return client.put<Entity>(`/entities/${entityId}`, request);
}

// Export audit trail
export interface AuditTrailExport {
  export_metadata: {
    export_date: string;
    project_id: string;
    project_name: string;
    entity_type_filter: string | null; // Backward compatibility
    entity_types: string[] | null; // Multi-select entity types
    total_entities: number;
    total_artifacts: number;
    total_jobs: number;
  };
  artifact_to_entity_mapping: Array<{
    artifact_id: string;
    filename: string;
    artifact_type: string;
    file_size_bytes: number | null;
    mime_type: string | null;
    sha256: string;
    source_type: string;
    created_at: string | null;
    entity_ids: string[];
  }>;
  artifacts: Array<{
    artifact_id: string;
    filename: string;
    artifact_type: string;
    file_size_bytes: number | null;
    mime_type: string | null;
    sha256: string;
    source_id: string | null;
    source_name: string | null;
    source_type: string;
    chunks: Array<{
      chunk_id: string;
      chunk_strategy: string;
      position: number;
      content_length: number;
      created_at: string | null;
    }>;
    num_chunks: number;
    num_entities: number;
    created_at: string | null;
  }>;
  job_statistics: {
    total_jobs: number;
    total_chunks_created: number;
    total_entities_created: number;
    total_evidence_entries: number;
    total_processing_time_seconds: number;
    average_processing_time_seconds: number;
    jobs: Array<{
      job_id: string;
      created_at: string | null;
      started_at: string | null;
      completed_at: string | null;
      processing_time_seconds: number | null;
      num_artifacts_processed: number;
      num_chunks_created: number;
      num_evidence_entries: number;
      entities_extracted: Record<string, number>;
      total_entities_created: number;
      chunking_strategy: string;
      warnings: string[];
    }>;
  };
  data_pipeline_methodology: {
    chunking_strategy: {
      primary_strategy: string;
      strategies_used: string[];
      description: string;
      policy_pack_config?: any;
    };
    entity_extraction_methodology: {
      approach: string;
      validation: string;
      policy_pack_config?: any;
      extraction_prompts?: any;
    };
    processing_approach: {
      description: string;
      average_processing_time_seconds: number;
      parallel_processing: boolean;
      error_handling: string;
    };
    quality_measures: {
      validation_protocols: any[];
      data_quality_checks: any[];
      warnings: string[];
    };
  };
  policy_pack_details: {
    id: string;
    name: string;
    version: string;
    description: string | null;
    rules: Record<string, any>;
    disclosure_text: string | null;
    created_at: string | null;
    updated_at: string | null;
  } | null;
  entity_statistics: {
    by_type: Record<string, number>;
    with_artifact_trace: number;
    without_artifact_trace: number;
  };
  sources: Array<{
    source_id: string;
    name: string;
    source_type: string;
    num_artifacts: number;
    created_at: string | null;
  }>;
  pfas_specific_details?: {
    tea_classification: Record<string, any>;
    unit_conversion_validation: Record<string, any>;
    export_formatting_rules: Record<string, any>;
  };
}

export async function exportAuditTrail(
  projectId: string,
  entityTypes?: string | string[],
  entityIds?: string[]
): Promise<AuditTrailExport> {
  const client = getAPIClient();
  
  // Support both single string and array of entity types
  const entityTypesParam = Array.isArray(entityTypes) 
    ? entityTypes.join(',') 
    : entityTypes;
  
  return client.get<AuditTrailExport>('/entities/export-audit-trail', {
    params: {
      project_id: projectId,
      entity_types: entityTypesParam,
      entity_ids: entityIds?.join(','),
    },
  });
}
